import React, { useEffect, useState } from "react";
import {
    Card, CardContent, CardMedia, Typography, Grid, List,
    ListItem, Divider, Box, Collapse, IconButton, Button, TextField, MenuItem
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { useBooking } from "./BookingContext";


const BookingValidation = ({ hotelData }) => {
    const { bookingData, bookingCode } = useBooking();
    const [expandedRoom, setExpandedRoom] = useState(null);
    const [netAmount, setNetAmount] = useState(0); // State to store NetAmount

    useEffect(() => {
        console.log("Booking Data from Context:", bookingData);
        // Set NetAmount from hotelData
        if (hotelData && hotelData.Rooms && hotelData.Rooms.length > 0) {
            setNetAmount(hotelData.Rooms[0].NetAmount);
        }
    }, [bookingData, hotelData]);

    if (!hotelData) return <Typography>Loading...</Typography>;

    const handleExpandClick = (index) => {
        setExpandedRoom(expandedRoom === index ? null : index);
    };

    const validationSchema = Yup.object().shape({
        guests: Yup.array().of(
            Yup.object().shape({
                title: Yup.string().required("Title is required"),
                firstName: Yup.string().required("First Name is required"),
                lastName: Yup.string().required("Last Name is required"),
                age: Yup.number()
                    .required("Age is required")
                    .test("is-adult", "Adults must be older than 18", function (value) {
                        return value > 18; // Ensure all adults are older than 18
                    }),
                phoneNo: Yup.string().required("Phone Number is required"),
                email: Yup.string().email("Invalid email").required("Email is required"),
                leadPassenger: Yup.boolean().required("Lead Passenger status is required")
            })
        ),
        children: Yup.array().of(
            Yup.object().shape({
                title: Yup.string().required("Title is required"),
                firstName: Yup.string().required("First Name is required"),
                lastName: Yup.string().required("Last Name is required"),
                age: Yup.number()
                    .required("Age is required")
                    .max(12, "Child age must be 18 or below"),
                phoneNo: Yup.string(),
                email: Yup.string().email("Invalid email"),
                leadPassenger: Yup.boolean().required("Lead Passenger status is required")
            })
        )
    });

    const initialValues = {
        guests: Array.from({ length: parseInt(bookingData.adults, 10) }, () => ({
            title: "Mr.",
            firstName: "",
            lastName: "",
            age: "",
            phoneNo: "",
            email: "",
            leadPassenger: false
        })),
        children: Array.from({ length: parseInt(bookingData.children, 10) }, () => ({
            title: "Mr.",
            firstName: "",
            lastName: "",
            age: "",
            phoneNo: "",
            email: "",
            leadPassenger: false
        })),
    };

    const handleSubmit = async (values) => {
        const bookingPayload = {
            BookingCode: bookingCode,
            GuestNationality: "IN",
            NetAmount: netAmount, // Use the stored NetAmount
            HotelRoomsDetails: [
                {
                    HotelPassenger: [
                        ...values.guests.map(guest => ({
                            Title: guest.title,
                            FirstName: guest.firstName,
                            MiddleName: "",
                            LastName: guest.lastName,
                            Email: guest.email,
                            PaxType: 1,
                            LeadPassenger: guest.leadPassenger,
                            Age: guest.age,
                            PassportNo: null,
                            PassportIssueDate: null,
                            PassportExpDate: null,
                            Phoneno: guest.phoneNo,
                            PaxId: 0,
                            GSTCompanyAddress: null,
                            GSTCompanyContactNumber: null,
                            GSTCompanyName: null,
                            GSTNumber: null,
                            GSTCompanyEmail: null
                        })),
                        ...values.children.map(child => ({
                            Title: child.title,
                            FirstName: child.firstName,
                            MiddleName: "",
                            LastName: child.lastName,
                            Email: child.email,
                            PaxType: 2,
                            LeadPassenger: child.leadPassenger,
                            Age: child.age,
                            PassportNo: null,
                            PassportIssueDate: null,
                            PassportExpDate: null,
                            Phoneno: child.phoneNo,
                            PaxId: 0,
                            GSTCompanyAddress: null,
                            GSTCompanyContactNumber: null,
                            GSTCompanyName: null,
                            GSTNumber: null,
                            GSTCompanyEmail: null
                        }))
                    ]
                }
            ]
        };

        try {
            // First API call to book the hotel
            const response = await fetch('https://api.ipaisa.co.in/myapp/hotels/book', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('jwtToken')}`
                },
                body: JSON.stringify(bookingPayload)
            });

            const result = await response.json();

            if (response.ok) {
                alert("Booking Successful!");

                // Check if BookingId is present in the response
                if (result.BookResult && result.BookResult.BookingId) {
                    const bookingId = result.BookResult.BookingId;

                    // Second API call to get booking details
                    const bookingDetailsResponse = await fetch('https://api.ipaisa.co.in/myapp/hotels/hotelbookingdetails', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${sessionStorage.getItem('jwtToken')}`
                        },
                        body: JSON.stringify({ bookingId })
                    });

                    const bookingDetailsResult = await bookingDetailsResponse.json();

                    if (bookingDetailsResponse.ok) {
                        console.log("Booking Details:", bookingDetailsResult);
                    } else {
                        alert(`Failed to fetch booking details: ${bookingDetailsResult.message}`);
                    }
                }
            } else {
                alert(`Booking Failed: ${result.message}`);
            }
        } catch (error) {
            alert("An error occurred while booking.");
        }
    };


    return (
        <Box sx={{ padding: "20px", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
            {/* Rooms Section */}
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1976d2", marginBottom: 2 }}>
                Rooms
            </Typography>
            <Grid container spacing={3}>
                {hotelData.Rooms.map((room, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card sx={{ boxShadow: 4, borderRadius: 2, overflow: "hidden" }}>
                            {room.ImageUrl && (
                                <CardMedia component="img" height="180" image={room.ImageUrl} alt={room.Name} />
                            )}
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                                    {room.Name.join(", ")}
                                </Typography>

                                <Typography variant="body2"><strong>Inclusion:</strong> {room.Inclusion}</Typography>
                                <Typography variant="body2"><strong>Total Fare:</strong> {room.TotalFare.toFixed(2)} {hotelData.Currency}</Typography>
                                <Typography variant="body2"><strong>Net Amount:</strong> {room.NetAmount.toFixed(2)}</Typography>
                                <Typography variant="body2"><strong>Refundable:</strong> {room.IsRefundable ? "Yes" : "No"}</Typography>
                                <Typography variant="body2"><strong>Meal Type:</strong> {room.MealType}</Typography>

                                <IconButton onClick={() => handleExpandClick(index)} sx={{ marginTop: 1 }} aria-expanded={expandedRoom === index}>
                                    <ExpandMoreIcon />
                                </IconButton>
                                <Collapse in={expandedRoom === index} timeout="auto" unmountOnExit>
                                    <Divider sx={{ marginY: 2 }} />
                                    {/* Room Promotions */}
                                    {room.RoomPromotion.length > 0 && (
                                        <Box sx={{ marginBottom: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#d32f2f" }}>Promotions:</Typography>
                                            <List dense>
                                                {room.RoomPromotion.map((promo, i) => (
                                                    <ListItem key={i} sx={{ paddingY: 0.5 }}>- {promo}</ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                    <Divider sx={{ marginY: 2 }} />
                                    {/* Amenities */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#0288d1" }}>Amenities:</Typography>
                                    <List dense>
                                        {room.Amenities.slice(0, 5).map((amenity, i) => (
                                            <ListItem key={i} sx={{ paddingY: 0.5 }}>- {amenity}</ListItem>
                                        ))}
                                    </List>
                                    {/* Cancellation Policies */}
                                    {room.CancelPolicies.length > 0 && (
                                        <Box sx={{ marginTop: 2 }}>
                                            <Divider sx={{ marginY: 2 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#e65100" }}>Cancellation Policies:</Typography>
                                            {room.CancelPolicies.map((policy, i) => (
                                                <Typography key={i} variant="body2">
                                                    From <strong>{policy.FromDate}</strong>, Charge:{" "}
                                                    <strong>{policy.CancellationCharge.toFixed(2)}%</strong>
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Collapse>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Divider sx={{ marginY: 3 }} />

            {/* Booking Form */}
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1976d2", marginBottom: 2 }}>
                Book a Room
            </Typography>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched }) => (
                    <Form>
                        {/* Adults Form */}
                        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2 }}>
                            Adults
                        </Typography>
                        <FieldArray name="guests">
                            {() => (
                                <>
                                    {values.guests.map((_, index) => (
                                        <Grid container spacing={2} key={index}>
                                            <Grid item xs={12} sm={6}>
                                                <Field as={TextField} name={`guests[${index}].title`} label="Title" fullWidth
                                                    error={touched.guests?.[index]?.title && !!errors.guests?.[index]?.title}
                                                    helperText={touched.guests?.[index]?.title && errors.guests?.[index]?.title} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field as={TextField} name={`guests[${index}].firstName`} label="First Name" fullWidth
                                                    error={touched.guests?.[index]?.firstName && !!errors.guests?.[index]?.firstName}
                                                    helperText={touched.guests?.[index]?.firstName && errors.guests?.[index]?.firstName} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field as={TextField} name={`guests[${index}].lastName`} label="Last Name" fullWidth
                                                    error={touched.guests?.[index]?.lastName && !!errors.guests?.[index]?.lastName}
                                                    helperText={touched.guests?.[index]?.lastName && errors.guests?.[index]?.lastName} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field as={TextField} name={`guests[${index}].age`} label="Age" type="number" fullWidth
                                                    error={touched.guests?.[index]?.age && !!errors.guests?.[index]?.age}
                                                    helperText={touched.guests?.[index]?.age && errors.guests?.[index]?.age} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field as={TextField} name={`guests[${index}].phoneNo`} label="Phone Number" fullWidth
                                                    error={touched.guests?.[index]?.phoneNo && !!errors.guests?.[index]?.phoneNo}
                                                    helperText={touched.guests?.[index]?.phoneNo && errors.guests?.[index]?.phoneNo} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field as={TextField} name={`guests[${index}].email`} label="Email" fullWidth
                                                    error={touched.guests?.[index]?.email && !!errors.guests?.[index]?.email}
                                                    helperText={touched.guests?.[index]?.email && errors.guests?.[index]?.email} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field as={TextField} name={`guests[${index}].leadPassenger`} label="Lead Passenger" select fullWidth
                                                    error={touched.guests?.[index]?.leadPassenger && !!errors.guests?.[index]?.leadPassenger}
                                                    helperText={touched.guests?.[index]?.leadPassenger && errors.guests?.[index]?.leadPassenger}>
                                                    <MenuItem value={true}>Yes</MenuItem>
                                                    <MenuItem value={false}>No</MenuItem>
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </>
                            )}
                        </FieldArray>

                        {/* Children Form */}
                        {parseInt(bookingData.children, 10) > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 3, marginBottom: 2 }}>
                                    Children
                                </Typography>
                                <FieldArray name="children">
                                    {() => (
                                        <>
                                            {values.children.map((_, index) => (
                                                <Grid container spacing={2} key={index}>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} name={`children[${index}].title`} label="Title" fullWidth
                                                            error={touched.children?.[index]?.title && !!errors.children?.[index]?.title}
                                                            helperText={touched.children?.[index]?.title && errors.children?.[index]?.title} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} name={`children[${index}].firstName`} label="First Name" fullWidth
                                                            error={touched.children?.[index]?.firstName && !!errors.children?.[index]?.firstName}
                                                            helperText={touched.children?.[index]?.firstName && errors.children?.[index]?.firstName} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} name={`children[${index}].lastName`} label="Last Name" fullWidth
                                                            error={touched.children?.[index]?.lastName && !!errors.children?.[index]?.lastName}
                                                            helperText={touched.children?.[index]?.lastName && errors.children?.[index]?.lastName} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} name={`children[${index}].age`} label="Age" type="number" fullWidth
                                                            error={touched.children?.[index]?.age && !!errors.children?.[index]?.age}
                                                            helperText={touched.children?.[index]?.age && errors.children?.[index]?.age} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} name={`children[${index}].phoneNo`} label="Phone Number" fullWidth
                                                            error={touched.children?.[index]?.phoneNo && !!errors.children?.[index]?.phoneNo}
                                                            helperText={touched.children?.[index]?.phoneNo && errors.children?.[index]?.phoneNo} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} name={`children[${index}].email`} label="Email" fullWidth
                                                            error={touched.children?.[index]?.email && !!errors.children?.[index]?.email}
                                                            helperText={touched.children?.[index]?.email && errors.children?.[index]?.email} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <Field as={TextField} name={`children[${index}].leadPassenger`} label="Lead Passenger" select fullWidth
                                                            error={touched.children?.[index]?.leadPassenger && !!errors.children?.[index]?.leadPassenger}
                                                            helperText={touched.children?.[index]?.leadPassenger && errors.children?.[index]?.leadPassenger}>
                                                            <MenuItem value={true}>Yes</MenuItem>
                                                            <MenuItem value={false}>No</MenuItem>
                                                        </Field>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </>
                                    )}
                                </FieldArray>
                            </>
                        )}

                        <Button type="submit" variant="contained" color="primary" sx={{ marginTop: 3 }}>
                            Submit Booking
                        </Button>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default BookingValidation;
